import React, { useMemo, useState } from 'react';
import {
  OFFER_KIND_EUR,
  OFFER_KIND_POURCENT,
  buildMontantOfferSummary,
  buildPourcentOfferSummary,
  syncAutoLibelleIfNeeded,
  isAutoGeneratedLibelle,
  previewStatutAffiche,
} from '../../utils/recompenseForm';
import { formatStatutLabel as formatBadgeLabel } from '../../utils/recompensePresentation';

function labelAffiche(code) {
  return formatBadgeLabel(code);
}

/**
 * F2.5a2 — Création/édition Montant offert ou Réduction en %.
 */
export default function RecompenseForm({
  mode = 'create',
  form,
  errors = {},
  submitting = false,
  readOnly = false,
  formError = null,
  onChange,
  onSubmit,
  onCancel,
  onSaveAsDraft,
  showSaveAsDraft = false,
}) {
  const [showAdvanced, setShowAdvanced] = useState(mode === 'edit');
  const offerKind =
    form.offerKind === OFFER_KIND_POURCENT ? OFFER_KIND_POURCENT : OFFER_KIND_EUR;
  const typeLocked = !!form.typeLocked || mode === 'edit';

  const summary = useMemo(() => {
    if (offerKind === OFFER_KIND_POURCENT) {
      return buildPourcentOfferSummary(form.coutPoints, form.valeurPourcent);
    }
    return buildMontantOfferSummary(form.coutPoints, form.valeurEuros);
  }, [offerKind, form.coutPoints, form.valeurEuros, form.valeurPourcent]);

  const affiche = useMemo(
    () => previewStatutAffiche(form.statut, form.validFrom, form.validTo),
    [form.statut, form.validFrom, form.validTo]
  );

  const setField = (key, value) => {
    if (readOnly || submitting) return;
    onChange?.({ ...form, [key]: value });
  };

  const selectOfferKind = (kind) => {
    if (readOnly || submitting || typeLocked) return;
    if (kind === offerKind) return;
    const next = {
      ...form,
      offerKind: kind,
      valeurEuros: kind === OFFER_KIND_EUR ? form.valeurEuros : '',
      valeurPourcent: kind === OFFER_KIND_POURCENT ? form.valeurPourcent : '',
    };
    const synced = syncAutoLibelleIfNeeded(
      next.libelle,
      kind,
      next.valeurEuros,
      next.valeurPourcent
    );
    // Si auto mais valeur du nouveau type encore vide → effacer l’ancien auto-libellé
    if (isAutoGeneratedLibelle(next.libelle) && !synced.synced) {
      next.libelle = '';
    } else {
      next.libelle = synced.libelle;
    }
    onChange?.(next);
  };

  /** Suit la valeur tant que le libellé est vide ou auto (jamais un texte personnalisé). */
  const applyAutoLibelle = (next, kind, eurosRaw, pctRaw) => {
    const { libelle } = syncAutoLibelleIfNeeded(next.libelle, kind, eurosRaw, pctRaw);
    next.libelle = libelle;
    return next;
  };

  const title = mode === 'edit' ? 'Modifier la récompense' : 'Ajouter une récompense';
  const submitLabel =
    mode === 'edit'
      ? submitting
        ? 'Enregistrement…'
        : 'Enregistrer'
      : submitting
        ? 'Création…'
        : 'Créer la récompense';

  return (
    <form
      className="bg-white border border-gray-200 rounded-xl p-5 space-y-5 shadow-sm"
      data-testid="recompense-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!submitting && !readOnly) onSubmit?.();
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600 mt-1">Que souhaitez-vous offrir ?</p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-gray-500 hover:text-gray-800"
          disabled={submitting}
        >
          Fermer
        </button>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-3 gap-2"
        role="radiogroup"
        aria-label="Type de récompense"
        data-testid="recompense-offer-kind"
      >
        <button
          type="button"
          role="radio"
          aria-checked={offerKind === OFFER_KIND_EUR}
          data-testid="recompense-kind-eur"
          disabled={readOnly || submitting || (typeLocked && offerKind !== OFFER_KIND_EUR)}
          onClick={() => selectOfferKind(OFFER_KIND_EUR)}
          className={`rounded-xl border-2 px-3 py-3 text-left transition ${
            offerKind === OFFER_KIND_EUR
              ? 'border-[#4A148C] bg-purple-50/70'
              : 'border-gray-200 bg-white hover:border-gray-300'
          } disabled:opacity-60`}
        >
          <p className="text-sm font-semibold text-gray-900">Montant offert</p>
          <p className="text-xs text-gray-600 mt-1">Réduction en euros</p>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={offerKind === OFFER_KIND_POURCENT}
          data-testid="recompense-kind-pourcent"
          disabled={
            readOnly || submitting || (typeLocked && offerKind !== OFFER_KIND_POURCENT)
          }
          onClick={() => selectOfferKind(OFFER_KIND_POURCENT)}
          className={`rounded-xl border-2 px-3 py-3 text-left transition ${
            offerKind === OFFER_KIND_POURCENT
              ? 'border-[#4A148C] bg-purple-50/70'
              : 'border-gray-200 bg-white hover:border-gray-300'
          } disabled:opacity-60`}
        >
          <p className="text-sm font-semibold text-gray-900">Réduction en %</p>
          <p className="text-xs text-gray-600 mt-1">Pourcentage sur la commande</p>
        </button>
        <div
          className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-3 text-left opacity-70"
          data-testid="recompense-kind-cadeau-soon"
          aria-disabled="true"
        >
          <p className="text-sm font-semibold text-gray-500">Produit / cadeau</p>
          <p className="text-xs text-gray-400 mt-1">Bientôt</p>
        </div>
      </div>

      {typeLocked && (
        <p className="text-xs text-gray-500" data-testid="recompense-type-locked">
          Type figé pour cette récompense — impossible de le changer à l’édition.
        </p>
      )}

      {formError && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
          role="alert"
          data-testid="recompense-form-error"
        >
          {formError}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {offerKind === OFFER_KIND_POURCENT ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="rec-pct">
              Pourcentage
            </label>
            <div className="flex items-center gap-2">
              <input
                id="rec-pct"
                data-testid="recompense-field-pourcent"
                type="number"
                min={0.01}
                max={100}
                step="0.01"
                value={form.valeurPourcent}
                disabled={readOnly || submitting}
                onChange={(e) => {
                  const valeurPourcent = e.target.value;
                  let next = { ...form, offerKind: OFFER_KIND_POURCENT, valeurPourcent };
                  next = applyAutoLibelle(
                    next,
                    OFFER_KIND_POURCENT,
                    form.valeurEuros,
                    valeurPourcent
                  );
                  onChange?.(next);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A148C] focus:border-transparent"
                aria-invalid={!!errors.valeurPourcent}
              />
              <span className="text-sm text-gray-600 shrink-0">%</span>
            </div>
            {errors.valeurPourcent && (
              <p className="text-sm text-red-600 mt-1">{errors.valeurPourcent}</p>
            )}
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="rec-euros">
              Montant offert
            </label>
            <div className="flex items-center gap-2">
              <input
                id="rec-euros"
                data-testid="recompense-field-euros"
                type="number"
                min={0.01}
                step="0.01"
                value={form.valeurEuros}
                disabled={readOnly || submitting}
                onChange={(e) => {
                  const valeurEuros = e.target.value;
                  let next = { ...form, offerKind: OFFER_KIND_EUR, valeurEuros };
                  next = applyAutoLibelle(next, OFFER_KIND_EUR, valeurEuros, form.valeurPourcent);
                  onChange?.(next);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A148C] focus:border-transparent"
                aria-invalid={!!errors.valeurEuros}
              />
              <span className="text-sm text-gray-600 shrink-0">€</span>
            </div>
            {errors.valeurEuros && (
              <p className="text-sm text-red-600 mt-1">{errors.valeurEuros}</p>
            )}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="rec-points">
            Points nécessaires
          </label>
          <div className="flex items-center gap-2">
            <input
              id="rec-points"
              data-testid="recompense-field-points"
              type="number"
              min={1}
              step={1}
              value={form.coutPoints}
              disabled={readOnly || submitting}
              onChange={(e) => setField('coutPoints', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A148C] focus:border-transparent"
              aria-invalid={!!errors.coutPoints}
            />
            <span className="text-sm text-gray-600 shrink-0">points</span>
          </div>
          {errors.coutPoints && (
            <p className="text-sm text-red-600 mt-1">{errors.coutPoints}</p>
          )}
        </div>
      </div>

      {summary && (
        <div
          className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-4 text-center space-y-1"
          data-testid="recompense-preview"
        >
          <p className="text-lg font-semibold text-gray-900">{summary.pointsLabel}</p>
          <p className="text-gray-400" aria-hidden>
            ↓
          </p>
          <p className="text-lg font-semibold text-[#4A148C]">{summary.offerLabel}</p>
        </div>
      )}

      <div>
        <button
          type="button"
          className="text-sm font-medium text-[#4A148C] hover:underline"
          onClick={() => setShowAdvanced((v) => !v)}
          data-testid="recompense-advanced-toggle"
        >
          {showAdvanced ? 'Masquer les options avancées' : 'Options avancées'}
        </button>
      </div>

      {showAdvanced && (
        <div className="space-y-4 border-t border-gray-100 pt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="rec-libelle">
              Nom de la récompense
            </label>
            <input
              id="rec-libelle"
              data-testid="recompense-field-libelle"
              type="text"
              value={form.libelle}
              disabled={readOnly || submitting}
              onChange={(e) => setField('libelle', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A148C] focus:border-transparent"
              aria-invalid={!!errors.libelle}
            />
            {errors.libelle && (
              <p className="text-sm text-red-600 mt-1">{errors.libelle}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="rec-desc">
              Description <span className="text-gray-400 font-normal">(optionnel)</span>
            </label>
            <textarea
              id="rec-desc"
              data-testid="recompense-field-description"
              rows={2}
              value={form.description}
              disabled={readOnly || submitting}
              onChange={(e) => setField('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A148C] focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="rec-from">
                Date de début <span className="text-gray-400 font-normal">(optionnel)</span>
              </label>
              <input
                id="rec-from"
                data-testid="recompense-field-from"
                type="date"
                value={form.validFrom}
                disabled={readOnly || submitting}
                onChange={(e) => setField('validFrom', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="rec-to">
                Date de fin <span className="text-gray-400 font-normal">(optionnel)</span>
              </label>
              <input
                id="rec-to"
                data-testid="recompense-field-to"
                type="date"
                value={form.validTo}
                disabled={readOnly || submitting}
                onChange={(e) => setField('validTo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                aria-invalid={!!errors.validTo}
              />
              {errors.validTo && (
                <p className="text-sm text-red-600 mt-1">{errors.validTo}</p>
              )}
            </div>
          </div>

          {!readOnly && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="rec-statut">
                État
              </label>
              <select
                id="rec-statut"
                data-testid="recompense-field-statut"
                value={form.statut === 'ARCHIVEE' ? 'BROUILLON' : form.statut}
                disabled={submitting || form.statut === 'ARCHIVEE'}
                onChange={(e) => setField('statut', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="BROUILLON">Brouillon</option>
                <option value="ACTIVE">Active</option>
                <option value="SUSPENDUE">Suspendue</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Affichage prévu : {labelAffiche(affiche)}. Les dates définissent si l’offre est
                planifiée ou expirée.
              </p>
              {errors.statut && <p className="text-sm text-red-600 mt-1">{errors.statut}</p>}
            </div>
          )}
        </div>
      )}

      {!showAdvanced && (
        <input
          type="hidden"
          data-testid="recompense-field-libelle"
          value={form.libelle}
          readOnly
        />
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        {!readOnly && (
          <button
            type="submit"
            data-testid="recompense-submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-[#4A148C] text-white hover:bg-[#6A1B9A] disabled:opacity-60"
          >
            {submitLabel}
          </button>
        )}
        {(showSaveAsDraft || showAdvanced) && !readOnly && (
          <button
            type="button"
            data-testid="recompense-save-draft"
            disabled={submitting}
            onClick={onSaveAsDraft}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-[#4A148C] text-[#4A148C] hover:bg-purple-50 disabled:opacity-60"
          >
            Garder en brouillon
          </button>
        )}
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Annuler
        </button>
      </div>
    </form>
  );
}
